import { useId, useState, type FormEvent } from "react";
import { removeKey, saveKey, type KeyStatus } from "./api";

// Each founder's own Anthropic API key (D-1472). The key is typed here once,
// sent to crm-api, checked with Anthropic, and stored encrypted on the server;
// the browser never keeps it (no localStorage) and crm-api never sends it back
// — only its last four characters.

export const CONSOLE_KEYS_URL = "https://console.anthropic.com/settings/keys";

export function KeyForm({ replacing = false, onSaved, onCancel }: { replacing?: boolean; onSaved: (s: KeyStatus) => void; onCancel?: () => void }) {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = useId();
  const helpId = useId();

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!key.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await saveKey(key.trim());
      if (r.ok) {
        setKey("");
        onSaved(r.status);
      } else {
        setError(r.message);
      }
    } catch (err) {
      setError(`Could not reach the CRM: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="vb-key" onSubmit={submit}>
      <h2 className="vb-key-title">{replacing ? "Replace your Anthropic API key" : "Use your own Anthropic API key"}</h2>
      <p id={helpId} className="muted small">
        The assistant runs your commands on your own key. Paste it once: it is checked with Anthropic, stored encrypted on
        the CRM server, and used only for your commands. It is never shown again, only its last four characters. Create
        one at{" "}
        <a href={CONSOLE_KEYS_URL} target="_blank" rel="noreferrer">console.anthropic.com</a>.
      </p>
      <div className="vb-row">
        <label className="vb-sr" htmlFor={id}>Anthropic API key</label>
        <input
          id={id}
          className="vb-input"
          type="password"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="sk-ant-…"
          autoComplete="off"
          spellCheck={false}
          aria-describedby={helpId}
          disabled={busy}
        />
        <button type="submit" className="btn primary" disabled={busy || !key.trim()}>{busy ? "Checking…" : "Save key"}</button>
        {onCancel ? <button type="button" className="btn ghost" onClick={onCancel} disabled={busy}>Cancel</button> : null}
      </div>
      {error ? <p className="error" role="alert">{error}</p> : null}
    </form>
  );
}

export function KeyLine({ status, onReplace, onRemoved }: { status: KeyStatus; onReplace: () => void; onRemoved: (s: KeyStatus) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      const r = await removeKey();
      if (r.ok) onRemoved(r.status);
      else setError(r.message);
    } catch (err) {
      setError(`Could not reach the CRM: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="vb-keyline">
      <p className="muted small">Your Anthropic key ending …{status.last4}</p>
      <button type="button" className="vb-link small" onClick={onReplace} disabled={busy}>Replace</button>
      <button type="button" className="vb-link small" onClick={remove} disabled={busy}>Remove</button>
      {error ? <p className="error" role="alert">{error}</p> : null}
    </div>
  );
}
